import { fetchAuthSession } from 'aws-amplify/auth';

import { WHISPERBI_SECURITY_API } from '../env';

const CONVERSATION_BASE_URL = `${WHISPERBI_SECURITY_API}/conversation`;
const CONVERSATIONS_BASE_URL = `${WHISPERBI_SECURITY_API}/conversations`;

const getAuthHeaders = async () => {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
    };
};

const makeApiRequest = async (url, options = {}) => {
    const headers = await getAuthHeaders();
    const response = await fetch(url, { ...options, headers: { ...headers, ...options.headers } });
    if (response.status === 404 && options.notFoundOk) {
        return null;
    }
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }
    return await response.json();
};

/**
 * Crea el registro de metadatos de una nueva conversación (entrada del sidebar).
 * @param {string} sessionId
 * @param {string} name
 * @param {string} userName
 * @param {string} [workspaceId] - Workspace al que pertenece el chat (opcional).
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const createConversation = async (sessionId, name, userName, workspaceId) => {
    try {
        if (!sessionId) throw new Error('sessionId is required');
        if (!userName) throw new Error('userName is required');
        const body = { session_id: sessionId, name, user_name: userName.toLowerCase() };
        if (workspaceId) {
            body.workspace_id = workspaceId;
        }
        const response = await makeApiRequest(CONVERSATION_BASE_URL, {
            method: 'POST',
            body: JSON.stringify(body)
        });
        return { success: true, data: response };
    } catch (error) {
        console.error('Error al crear la conversación:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Lista las conversaciones (sesiones) de un usuario para el sidebar.
 * @param {string} userName
 * @returns {Promise<{success: boolean, data?: object[], error?: string}>}
 */
export const getSessionsByUser = async (userName) => {
    try {
        if (!userName) throw new Error('userName is required');
        const url = `${CONVERSATIONS_BASE_URL}?user_name=${encodeURIComponent(userName.toLowerCase())}`;
        const response = await makeApiRequest(url, { method: 'GET' });
        return { success: true, data: response.data || [] };
    } catch (error) {
        console.error('Error al listar las conversaciones:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Obtiene una PÁGINA del historial de turnos de una conversación
 * (control de costos/rendimiento, 2026-09-24): antes traía el historial
 * completo en una sola invocación; ahora el backend pagina de verdad
 * (últimos N turnos por página, con cursor para "cargar más" antiguos),
 * incluyendo conversaciones guardadas antes de este cambio (el backend
 * cae a paginar en memoria el blob legacy — ver `source` en la respuesta,
 * `"turns"` | `"legacy"`, y el mismo campo dentro de cada turno).
 * @param {string} sessionId
 * @param {{limit?: number, cursor?: string}} [options]
 * @returns {Promise<{success: boolean, data?: {session_id, name, datetime, workspace_id, turns: Array, next_cursor: string|null, source: string}, error?: string}>}
 */
export const getConversation = async (sessionId, { limit, cursor } = {}) => {
    try {
        if (!sessionId) throw new Error('sessionId is required');
        const params = new URLSearchParams();
        if (limit) params.set('limit', String(limit));
        if (cursor) params.set('cursor', cursor);
        const qs = params.toString();
        const url = `${CONVERSATION_BASE_URL}/${encodeURIComponent(sessionId)}${qs ? `?${qs}` : ''}`;
        // notFoundOk: una conversación sin historial guardado (aún) es un
        // estado normal, no un error — evita ruido de 404 en consola.
        const response = await makeApiRequest(url, { method: 'GET', notFoundOk: true });
        return { success: true, data: response ? response.data : null };
    } catch (error) {
        console.error('Error al obtener la conversación:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Agrega UN turno (una pregunta o una respuesta) al historial de una
 * conversación (control de costos/rendimiento, 2026-09-24). Reemplaza a
 * `saveConversationResponse` para el flujo normal: en vez de reenviar el
 * array completo de la conversación en cada guardado (lo que la hacía
 * lenta de abrir/guardar y la acercaba en silencio al límite de 400KB por
 * item de DynamoDB), cada turno se persiste por separado.
 * @param {string} sessionId
 * @param {object} turn - El objeto de turno tal cual vive en el array `answers` (p.ej. `{query}` o la respuesta del agente).
 * @param {object} [control] - El `controlAnswers[index]` correspondiente (p.ej. `{current_tab_view}`).
 * @returns {Promise<{success: boolean, data?: {turn_index: number}, error?: string}>}
 */
export const appendConversationTurn = async (sessionId, turn, control) => {
    try {
        if (!sessionId) throw new Error('sessionId is required');
        const url = `${CONVERSATION_BASE_URL}/${encodeURIComponent(sessionId)}/turn`;
        const response = await makeApiRequest(url, {
            method: 'POST',
            body: JSON.stringify({ turn, control: control || {} })
        });
        return { success: true, data: response };
    } catch (error) {
        console.error('Error al guardar el turno de la conversación:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Sobreescribe un turno YA creado (el `turn_index` que devolvió
 * `appendConversationTurn`). Hace falta porque un turno de asistente
 * sigue cambiando DESPUÉS de crearse (la gráfica que llega en segundo
 * plano vía `/agent/chart-data`, el feedback de pulgar arriba/abajo) --
 * sin esto esos datos nunca se persistirían para la próxima vez que se
 * abra el historial.
 * @param {string} sessionId
 * @param {number} turnIndex
 * @param {object} turn
 * @param {object} [control]
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const updateConversationTurn = async (sessionId, turnIndex, turn, control) => {
    try {
        if (!sessionId) throw new Error('sessionId is required');
        if (turnIndex === undefined || turnIndex === null) throw new Error('turnIndex is required');
        const url = `${CONVERSATION_BASE_URL}/${encodeURIComponent(sessionId)}/turn/${encodeURIComponent(turnIndex)}`;
        const response = await makeApiRequest(url, {
            method: 'PUT',
            body: JSON.stringify({ turn, control: control || {} })
        });
        return { success: true, data: response };
    } catch (error) {
        console.error('Error al actualizar el turno de la conversación:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Guarda (upsert) el historial COMPLETO de respuestas de una conversación.
 * LEGACY: se mantiene por compatibilidad con el endpoint del backend
 * (recuperación manual), pero el flujo normal de guardado usa
 * `appendConversationTurn` turno a turno — ver esa función.
 * @param {string} sessionId
 * @param {Array} answers
 * @param {Array} controlAnswers
 * @param {number} totalAnswers
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const saveConversationResponse = async (sessionId, answers, controlAnswers, totalAnswers) => {
    try {
        if (!sessionId) throw new Error('sessionId is required');
        const url = `${CONVERSATION_BASE_URL}/${encodeURIComponent(sessionId)}`;
        const response = await makeApiRequest(url, {
            method: 'PUT',
            body: JSON.stringify({ answers, controlAnswers, totalAnswers })
        });
        return { success: true, data: response };
    } catch (error) {
        console.error('Error al guardar la conversación:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Renombra una conversación.
 * @param {string} userName
 * @param {string} sessionId
 * @param {string} newName
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const renameConversation = async (userName, sessionId, newName) => {
    try {
        if (!sessionId) throw new Error('sessionId is required');
        if (!userName) throw new Error('userName is required');
        const url = `${CONVERSATION_BASE_URL}/${encodeURIComponent(sessionId)}/name`;
        const response = await makeApiRequest(url, {
            method: 'PUT',
            body: JSON.stringify({ user_name: userName.toLowerCase(), name: newName })
        });
        return { success: true, data: response };
    } catch (error) {
        console.error('Error al renombrar la conversación:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Elimina una conversación.
 * @param {string} sessionId
 * @param {string} userName
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const deleteConversation = async (sessionId, userName) => {
    try {
        if (!sessionId) throw new Error('sessionId is required');
        if (!userName) throw new Error('userName is required');
        const url = `${CONVERSATION_BASE_URL}/${encodeURIComponent(sessionId)}?user_name=${encodeURIComponent(userName.toLowerCase())}`;
        const response = await makeApiRequest(url, { method: 'DELETE' });
        return { success: true, data: response };
    } catch (error) {
        console.error('Error al eliminar la conversación:', error);
        return { success: false, error: error.message };
    }
};
